import * as React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useColorMode } from './ColorModeSelect';

interface ColorModeIconDropdownProps {
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

export default function ColorModeIconDropdown({
    className,
    size = 'sm',
}: ColorModeIconDropdownProps) {
    const { mode, setMode } = useColorMode();

    const handleModeChange = (targetMode: 'system' | 'light' | 'dark') => {
        setMode(targetMode);
    };

    const getIcon = (mode: 'system' | 'light' | 'dark') => {
        switch (mode) {
            case 'light':
                return <Sun className="h-4 w-4" />;
            case 'dark':
                return <Moon className="h-4 w-4" />;
            default:
                return <Monitor className="h-4 w-4" />;
        }
    };

    const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
        switch (size) {
            case 'sm':
                return 'h-8 w-8';
            case 'md':
                return 'h-10 w-10';
            case 'lg':
                return 'h-12 w-12';
            default:
                return 'h-8 w-8';
        }
    };

    if (!mode) {
        return (
            <div
                data-screenshot="toggle-mode"
                className={`inline-flex ${getSizeClasses(size)} rounded-md border border-border ${className || ''}`}
            />
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className={`${getSizeClasses(size)} p-0 ${className || ''}`}
                    data-screenshot="toggle-mode"
                >
                    {getIcon(mode)}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                    onClick={() => handleModeChange('system')}
                    className={mode === 'system' ? 'bg-accent' : ''}
                >
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleModeChange('light')}
                    className={mode === 'light' ? 'bg-accent' : ''}
                >
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleModeChange('dark')}
                    className={mode === 'dark' ? 'bg-accent' : ''}
                >
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
